import { useEffect, useMemo, useState } from 'react'

import { useGetDemographicAndHousingCharacteristicsQuery, useGetEducationalAttainmentQuery, useGetFinancialCharacteristicsQuery } from 'api/censusDataApi'

export const CENSUS_DATA_LOAD_TIMEOUT_MS = 30_000

export interface HouseholdCensusDataResult {
  demographicData: ReturnType<typeof useGetDemographicAndHousingCharacteristicsQuery>['data']
  educationData: ReturnType<typeof useGetEducationalAttainmentQuery>['data']
  financialData: ReturnType<typeof useGetFinancialCharacteristicsQuery>['data']
  isWaiting: boolean
  timedOutWithoutData: boolean
  hasCensusData: boolean
}

export function useHouseholdCensusData(householdZipCodes: string[]): HouseholdCensusDataResult {
  const skip = householdZipCodes.length === 0

  const { data: demographicData, isFetching: isDemographicFetching } = useGetDemographicAndHousingCharacteristicsQuery({ zipCodes: householdZipCodes }, { skip })
  const { data: educationData, isFetching: isEducationFetching } = useGetEducationalAttainmentQuery({ zipCodes: householdZipCodes }, { skip })
  const { data: financialData, isFetching: isFinancialFetching } = useGetFinancialCharacteristicsQuery({ zipCodes: householdZipCodes }, { skip })

  const zipCodesKey = useMemo(() => [...householdZipCodes].sort().join(','), [householdZipCodes])

  const [hasTimedOut, setHasTimedOut] = useState(false)

  useEffect(() => {
    setHasTimedOut(false)
    if (!zipCodesKey) {
      return
    }
    const timer = setTimeout(() => setHasTimedOut(true), CENSUS_DATA_LOAD_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [zipCodesKey])

  const hasCensusData = skip || (demographicData !== undefined && educationData !== undefined && financialData !== undefined)
  const isAnyFetching = isDemographicFetching || isEducationFetching || isFinancialFetching
  const isWaiting = !skip && isAnyFetching && !hasTimedOut && !hasCensusData
  const timedOutWithoutData = !skip && hasTimedOut && !hasCensusData

  return { demographicData, educationData, financialData, isWaiting, timedOutWithoutData, hasCensusData }
}
